import { supabase } from '@/lib/supabase/client';

export interface PackingSlip {
  id: string;
  location_id: string;
  filename: string;
  shipment_ids: string[];
  created_at: string;
  created_by: string | null;
  notes: string | null;
}

export interface PackingSlipInsert {
  location_id: string;
  filename: string;
  shipment_ids: string[];
  notes?: string | null;
}

export interface PackingSlipUpdate {
  notes?: string | null;
}

export const packingSlipsService = {
  async getAll(): Promise<PackingSlip[]> {
    try {
      const { data, error } = await supabase
        .from('packing_slips')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching packing slips:', error);
        console.error('Error details:', JSON.stringify(error, null, 2));
        return [];
      }

      console.log('Fetched packing slips:', data?.length || 0, 'items');
      return (data as PackingSlip[]) || [];
    } catch (error) {
      console.error('Error fetching packing slips:', error);
      return [];
    }
  },

  async getById(id: string): Promise<PackingSlip | null> {
    try {
      const { data, error } = await supabase
        .from('packing_slips')
        .select('*')
        .eq('id', id)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          return null;
        }
        console.error('Error fetching packing slip:', error);
        return null;
      }

      return (data as PackingSlip) || null;
    } catch (error) {
      console.error('Error fetching packing slip:', error);
      return null;
    }
  },

  async getByLocationId(locationId: string): Promise<PackingSlip[]> {
    try {
      const { data, error } = await supabase
        .from('packing_slips')
        .select('*')
        .eq('location_id', locationId)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching packing slips by location:', error);
        return [];
      }

      return (data as PackingSlip[]) || [];
    } catch (error) {
      console.error('Error fetching packing slips by location:', error);
      return [];
    }
  },

  async create(data: PackingSlipInsert): Promise<PackingSlip> {
    try {
      console.log('Creating packing slip with data:', {
        location_id: data.location_id,
        filename: data.filename,
        shipment_ids: data.shipment_ids,
      });

      const { data: newPackingSlip, error } = await supabase
        .from('packing_slips')
        .insert({
          location_id: data.location_id,
          filename: data.filename,
          shipment_ids: data.shipment_ids,
          notes: data.notes ?? null,
        })
        .select()
        .single();

      if (error) {
        console.error('Error creating packing slip:', error);
        console.error('Error details:', JSON.stringify(error, null, 2));
        throw new Error(`Failed to create packing slip: ${error.message}`);
      }

      if (!newPackingSlip) {
        throw new Error('Packing slip created but no data returned');
      }

      console.log('Packing slip created successfully:', newPackingSlip);
      return newPackingSlip as PackingSlip;
    } catch (error) {
      console.error('Error creating packing slip:', error);
      throw error;
    }
  },

  async update(id: string, data: PackingSlipUpdate): Promise<PackingSlip> {
    try {
      const updateData: Record<string, unknown> = {};
      if (data.notes !== undefined) updateData.notes = data.notes;

      const { data: updatedPackingSlip, error } = await supabase
        .from('packing_slips')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        console.error('Error updating packing slip:', error);
        throw new Error(`Failed to update packing slip: ${error.message}`);
      }

      if (!updatedPackingSlip) {
        throw new Error('Packing slip not found');
      }

      return updatedPackingSlip as PackingSlip;
    } catch (error) {
      console.error('Error updating packing slip:', error);
      throw error;
    }
  },

  async delete(id: string): Promise<void> {
    try {
      const { error } = await supabase.from('packing_slips').delete().eq('id', id);

      if (error) {
        console.error('Error deleting packing slip:', error);
        throw new Error(`Failed to delete packing slip: ${error.message}`);
      }
    } catch (error) {
      console.error('Error deleting packing slip:', error);
      throw error;
    }
  },
};

